/**
 * 家长录音面板：**给读错音的词录一段自己的声音**（覆盖浏览器语音合成）。
 *
 * ## 为什么这是本模块唯一能纠正读音的地方
 *
 * 技术事实：浏览器的 `speechSynthesis` **只能读文字，喂不了拼音**。所以多音字
 * （"银行"的"行"、"长大"的"长"）它一定会读错，而读音读错就是**把孩子教错**。
 * 第一步的"人工覆盖拼音"解决的是**显示与数据**（界面上那串 `yín háng`），
 * 解决不了**朗读**。于是只有两条路：换一个能喂拼音的服务端 TTS（要密钥、要钱、
 * 要新的部署面），或者**让家长录一遍**。这里选后者。
 *
 * ## 怎么录（与后端同一条口径）
 *
 * `MediaRecorder` → `audio/webm;codecs=opus`（首选）→ Blob → base64 →
 * `PUT /students/:id/dictation/audios/:wordId`（**按词覆盖**，一个词只留一段）。
 *
 * 体积上限（40 万 base64 字符 ≈ 300 KB 音频）与数据库的 CHECK 是**同一个常量**
 * （`shared/dictation/recording.ts`），并且判定发生在**上传之前**：
 * 上传超限时 API 的 1 MB `bodyLimit` 会在路由之前把请求挡掉，
 * 家长只会看到一句笼统的"请求格式有误"，根本不知道是录音太长。
 *
 * ## 为什么没有文件存储
 *
 * 本项目没有 multer、没有上传卷、worker 也不写文件。为几秒的片段新建一套文件存储
 * （目录 + 静态服务 + 备份 + 清理 + 权限）是不成比例的，而这点数据量 Postgres
 * 完全放得下。所以音频以 base64 存进 `dictation_word_audio.audio_base64`，
 * 播放时由 `GET .../audios/:wordId` 返回 `data:` URL（`<audio src>` **不能**带
 * Authorization 头，所以不能直接指向接口）。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/Bits';
import { Input } from '../ui/Input';
import { useToast } from '../ui/Toast';
import {
  IconAlertTriangle,
  IconCheck,
  IconMic,
  IconRotateCcw,
  IconTrash,
  IconVolume2,
} from '../ui/Icons';
import { formatDateTimeLocal } from '../../lib/utils';
import { toErrorMessage } from '../../lib/api/error';
import { listWords } from '../../lib/api/dictation';
import * as sessionApi from '../../lib/api/dictationSessions';
import type { AudioListItemView } from '../../lib/api/dictationSessions';
import type { WordView } from '../../lib/api/dictation';
import { useDictationRecorder } from '../../lib/useDictationRecorder';
import { GRADES, audioFormatLabel, formatBytes, gradeLabel } from '@fht/shared';

export interface RecorderPanelProps {
  studentId: string;
  studentNickname: string;
  canEdit: boolean;
  /** `?wordId=` 带过来的词（从某个会话里点"给这个词录音"跳进来时会预选）。 */
  focusWordId: string | null;
  /**
   * `?wordText=` 带过来的词面。
   *
   * 为什么要它：录音页要**按关键字查一次**才能拿到那个词的完整信息
   * （字表接口没有"按 id 查一个词"）。只给 id 的话，页面得先知道搜什么 ——
   * 于是那个按钮会变成"点了没反应"。带上词面，这里可以直接拿它当关键字。
   */
  focusWordText: string | null;
  audios: AudioListItemView[] | null;
  audiosMissing: boolean;
  total: number | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onChanged: () => void;
}

export function DictationRecorderPanel({
  studentId,
  studentNickname,
  canEdit,
  focusWordId,
  focusWordText,
  audios,
  audiosMissing,
  total,
  loading,
  error,
  onRetry,
  onChanged,
}: RecorderPanelProps) {
  const toast = useToast();
  /**
   * 关键字初值 = 从会话里带过来的词面。
   *
   * ⚠️ 这样"给这个词录音"才是**一步到位**的：进页面就自动搜到那个词，
   *    家长只需要点「开始录音」。不这样做的话他得自己再打一遍那个字。
   */
  const [keyword, setKeyword] = useState(focusWordText ?? '');
  const [gradeCode, setGradeCode] = useState('');
  const [selected, setSelected] = useState<WordView | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const recorder = useDictationRecorder();

  /**
   * 候选词：按关键字 / 年级从字表里查（**复用第一步的词语列表接口**，不新造一个）。
   *
   * ⚠️ 只在家长输入了关键字或选了年级时才查：这一页一进来就发一个"全部词语"
   *    的请求（最多 500 条）既慢又没意义 —— 他的目的是找某一个词。
   */
  const wordsQuery = useQuery({
    queryKey: ['dictation-recorder-words', studentId, keyword, gradeCode] as const,
    queryFn: () =>
      listWords(
        /**
         * ⚠️ 这里的"孩子"只是**权限锚点**（字表是站点资产，换个孩子看到的是同一份）。
         *   它同时是"这次录音是给谁的"的锚点：录音的可见范围按这个孩子的家长算
         *   （见 `dictation-sessions.service.ts` 的 `loadAudioForWords`）。
         */
        { kind: 'parent', studentId },
        {
          ...(keyword.trim() === '' ? {} : { keyword: keyword.trim() }),
          ...(gradeCode === '' ? {} : { gradeCode }),
          limit: 60,
        },
      ),
    enabled: keyword.trim() !== '' || gradeCode !== '',
  });

  /** 已经录过音的词 id（列表里标出"已录音"）。 */
  const recordedIds = useMemo(
    () => new Set((audios ?? []).map((item) => item.wordId)),
    [audios],
  );

  /**
   * `?wordId=` 带过来时，从查到的候选里把它选上（只选一次）。
   *
   * ⚠️ 依赖里必须有 `keyword`：初始关键字就是那个词面，所以这一遍查询回来时
   *    才可能命中。只依赖 `wordsQuery.data` 会在"查询结果还没到"时白白跑一遍。
   */
  useEffect(() => {
    if (focusWordId === null || focusWordId === '' || selected !== null) return;
    const hit = (wordsQuery.data?.words ?? []).find((word) => word.id === focusWordId);
    if (hit !== undefined) setSelected(hit);
  }, [focusWordId, selected, wordsQuery.data, keyword]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (recorder.clip === null || selected === null) {
        throw new Error('还没有录到声音。');
      }
      return sessionApi.putAudio(studentId, selected.id, {
        mimeType: recorder.clip.mimeType,
        audioBase64: recorder.clip.base64,
        byteSize: recorder.clip.bytes,
        durationMs: recorder.clip.durationMs,
      });
    },
    onSuccess: (saved) => {
      toast.showToast(saved.replaced ? '已换成你新录的这一段' : '录音已保存', 'success');
      setSaveError(null);
      recorder.reset();
      setSelected(null);
      onChanged();
    },
    onError: (err: unknown) => setSaveError(toErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (wordId: string) => sessionApi.deleteAudio(studentId, wordId),
    onSuccess: (deleted) => {
      toast.showToast(deleted ? '已删掉这段录音，这个词会用浏览器语音读' : '本来就没有录音', 'info');
      onChanged();
    },
    onError: (err: unknown) => toast.showToast(`删除失败：${toErrorMessage(err)}`, 'error'),
  });

  /** 试听一段（`GET .../audios/:wordId` 给人话，拿不到就当"没有录音"）。 */
  const preview = async (wordId: string) => {
    setPlayingId(wordId);
    try {
      const audio = await sessionApi.getAudio(studentId, wordId);
      if (audio === null) {
        toast.showToast('这段录音取不到了（可能刚被删掉）', 'error');
        onChanged();
        return;
      }
      audioRef.current?.pause();
      const element = new Audio(audio.dataUrl);
      audioRef.current = element;
      element.onended = () => setPlayingId(null);
      element.onerror = () => {
        setPlayingId(null);
        toast.showToast('这段录音在这台设备上放不出来', 'error');
      };
      await element.play();
    } catch (err) {
      setPlayingId(null);
      toast.showToast(`试听失败：${toErrorMessage(err)}`, 'error');
    }
  };

  const clipVerdict = recorder.clip?.verdict ?? null;
  const canSave = canEdit && recorder.clip !== null && clipVerdict?.code !== 'error' && selected !== null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="朗读录音（替换读错音的词）"
          description="浏览器语音读多音字一定会读错。给这些词录一遍你自己的声音，听写时就会放你的录音。"
        />
        <ul className="space-y-1 text-sm text-slate-600">
          <li>· 一个词只留最近录的那一段（重录就是覆盖）。</li>
          <li>· 录的时候离麦克风近一点、把词读清楚，一两秒就够。</li>
          <li>
            · 每段最多约 {formatBytes(300_000)}（约 20 秒），超过就存不下了 —— 界面会在上传前拦住你。
          </li>
          <li>· 录音存在服务器里（不是只在这台手机上），家里别的手机也放得出来。</li>
        </ul>
      </Card>

      {/* ============================================ 找词 + 录音 */}
      <Card>
        <CardHeader
          title="找一个词来录"
          description={
            focusWordId !== null
              ? '你是从某次听写里点进来的 —— 下面找一下那个词就能录。'
              : '按关键字或年级找。想录的词不在结果里就换个关键字。'
          }
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="搜词（一个字或一个词）"
            value={keyword}
            maxLength={32}
            placeholder="例如：银行"
            disabled={!canEdit}
            onChange={(event) => setKeyword(event.target.value)}
          />
          <div>
            <label
              className="block text-sm font-medium text-slate-700"
              htmlFor="recorder-grade"
            >
              年级
            </label>
            <select
              id="recorder-grade"
              className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base"
              value={gradeCode}
              disabled={!canEdit}
              onChange={(event) => setGradeCode(event.target.value)}
            >
              <option value="">不限年级</option>
              {GRADES.map((grade) => (
                <option key={grade.code} value={grade.code}>
                  {gradeLabel(grade.code)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {wordsQuery.isError && (
          <ErrorState
            compact
            message={`没查到词语：${toErrorMessage(wordsQuery.error)}`}
            onRetry={() => void wordsQuery.refetch()}
          />
        )}

        {keyword.trim() !== '' || gradeCode !== '' ? (
          wordsQuery.isLoading ? (
            <Skeleton className="mt-3 h-16 w-full" />
          ) : wordsQuery.data?.words === null ? (
            <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              没拿到词语列表（后端返回的字段名与预期不一致）—— 这不是"没有这个词"。
            </p>
          ) : (wordsQuery.data?.words?.length ?? 0) === 0 ? (
            <p className="mt-3 text-sm text-slate-500">没找到匹配的词。</p>
          ) : (
            <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {(wordsQuery.data?.words ?? []).map((word) => (
                <li key={word.id} className="min-w-0">
                  <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => {
                      recorder.reset();
                      setSaveError(null);
                      setSelected(word);
                    }}
                    className={
                      selected?.id === word.id
                        ? 'w-full rounded-xl border border-brand-400 bg-brand-50 px-3 py-2 text-left'
                        : 'w-full rounded-xl border border-slate-200 px-3 py-2 text-left hover:bg-slate-50'
                    }
                  >
                    {/* ⚠️ 2026-10-17：**拼音在上、词在下**（与字卡同一个排法）。 */}
                    <span className="block h-4 truncate font-mono text-xs leading-4 text-slate-500">
                      {word.pinyin === '' ? '（没有读音）' : word.pinyin}
                    </span>
                    <span className="block truncate text-base font-semibold text-slate-900">
                      {word.wordText}
                    </span>
                    {recordedIds.has(word.id) && (
                      <span className="mt-0.5 block text-[11px] text-emerald-700">已有录音</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <p className="mt-3 text-sm text-slate-500">
            上面填一个字或选一个年级，就会列出可以录的词。
          </p>
        )}
      </Card>

      {/* ============================================ 录音机 */}
      <Card>
        <CardHeader
          title={selected === null ? '录音' : `正在给「${selected.wordText}」录音`}
          description={
            selected === null
              ? '先在上面选中一个词。'
              : '按一下开始，把词读清楚，再按一下结束。可以反复重录。'
          }
          actionAlign="start"
          action={
            selected !== null ? (
              <div className="flex basis-full flex-wrap items-center gap-2">
                <Badge tone="brand">{selected.wordText}</Badge>
                <Badge tone={recordedIds.has(selected.id) ? 'success' : 'neutral'}>
                  {recordedIds.has(selected.id) ? '这个词已有录音（保存会覆盖）' : '这个词还没有录音'}
                </Badge>
              </div>
            ) : undefined
          }
        />

        {!recorder.supported && (
          <p className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span className="mt-0.5 shrink-0">
              <IconAlertTriangle size={16} />
            </span>
            <span>
              这台设备/浏览器不支持录音（没有麦克风或 `MediaRecorder` 不可用）。
              这些词会继续用浏览器语音朗读 —— 多音字可能读错，建议换一台设备录。
            </span>
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {recorder.status === 'recording' ? (
            <Button
              variant="danger"
              icon={<IconCheck size={18} />}
              onClick={() => recorder.stop()}
            >
              结束录音（已录 {(recorder.elapsedMs / 1000).toFixed(1)} 秒）
            </Button>
          ) : (
            <Button
              disabled={!canEdit || selected === null || !recorder.supported}
              icon={<IconMic size={18} />}
              onClick={() => void recorder.start()}
            >
              {recorder.clip === null ? '开始录音' : '重新录一遍'}
            </Button>
          )}
          {recorder.clip !== null && (
            <>
              {/* 试听：直接用本地 blob（还没上传），不走接口 */}
              <audio controls src={recorder.clip.previewUrl} className="h-10 max-w-full" />
              <Button
                variant="secondary"
                icon={<IconVolume2 size={18} />}
                loading={saveMutation.isPending}
                disabled={!canSave}
                onClick={() => {
                  setSaveError(null);
                  saveMutation.mutate();
                }}
              >
                保存这段录音
              </Button>
            </>
          )}
        </div>

        {/* 体积 / 时长反馈（**上传之前**就说清楚，不能等接口报错） */}
        {recorder.clip !== null && clipVerdict !== null && (
          <div
            className={
              clipVerdict.code === 'error'
                ? 'mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700'
                : clipVerdict.code === 'warn'
                  ? 'mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'
                  : 'mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600'
            }
          >
            <p>
              这段录音 {formatBytes(recorder.clip.bytes)}
              {recorder.clip.durationMs === null
                ? '（时长没测出来）'
                : ` · ${(recorder.clip.durationMs / 1000).toFixed(1)} 秒`}
              {' · '}
              格式 {audioFormatLabel(recorder.clip.mimeType)}
            </p>
            {clipVerdict.message !== '' && <p className="mt-0.5">{clipVerdict.message}</p>}
            {recorder.clip.tooLong && (
              <p className="mt-0.5">
                建议每个词的录音控制在 20 秒以内 —— 太长会占地方，也没必要。
              </p>
            )}
            {recorder.bitsPerSecond !== null && (
              <p className="mt-0.5 text-xs">
                按这个码率（约 {Math.round(recorder.bitsPerSecond / 1000)} kbps），
                还能录很久，不用担心。
              </p>
            )}
          </div>
        )}

        {recorder.error !== null && (
          <p role="alert" className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {recorder.error}
          </p>
        )}
        {saveError !== null && (
          <p role="alert" className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {saveError}
          </p>
        )}
        {!canEdit && (
          <p className="mt-3 text-sm text-amber-700">
            你是「仅查看」权限，看得到但改不了 {studentNickname} 的录音。
          </p>
        )}
      </Card>

      {/* ============================================ 已有录音清单 */}
      <Card>
        <CardHeader
          title="已经录过音的词"
          description="听写时这些词会放你的录音，其它的用浏览器语音。"
          actionAlign="start"
          action={
            <div className="flex basis-full flex-wrap items-center gap-2">
              <Badge tone={total === null ? 'warn' : 'neutral'}>
                共 {total === null ? '—' : total} 个
              </Badge>
              {audios !== null && <Badge>{audios.length} 个在下面</Badge>}
            </div>
          }
        />

        {error !== null && (
          <ErrorState compact message={error} onRetry={onRetry} />
        )}

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : audiosMissing ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            没拿到录音清单（后端返回的字段名与预期不一致）—— 这不是"一个都没录过"。
          </p>
        ) : (audios ?? []).length === 0 ? (
          <EmptyState
            icon="🎙️"
            title="还没有录过音"
            description="多音字（银行、长大这类）建议录一遍：浏览器语音一定会把声调读错。"
          />
        ) : (
          <ul className="space-y-2">
            {(audios ?? []).map((item) => (
              <li
                key={item.wordId}
                className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-slate-900">{item.wordText}</p>
                  <p className="truncate text-xs text-slate-500">
                    {formatBytes(item.byteSize)}
                    {item.durationMs === null
                      ? ' · 时长没测出来'
                      : ` · ${(item.durationMs / 1000).toFixed(1)} 秒`}
                    {/*
                      ⚠️ 「更新于」以前是**裸切 ISO 字符串**（`updatedAt.slice(0, 16).replace('T', ' ')`）：
                      那是 **UTC 时间**，比省年份的旧函数还糟 —— 既没年份，也不是家庭时区
                      （服务器在 Asia/Tokyo、家庭在 Asia/Shanghai）。这里按 `lib/utils.ts` 的口径改成
                      `formatDateTimeLocal`：这是"嵌在句子中间的短标签"（` · 更新于 …`），
                      与同一模块 `ParentDictationReviewPage` 里"布置于/开始于/读完于"用的是同一个函数
                      （'MM/DD HH:mm' + **家庭时区**）；加年份只会把这一行挤长，台账性质的时间才用
                      `formatDateTimeFull`。
                      它同时把**空值**接住了：`formatDateTimeLocal(null | undefined | '')` → `'—'`，
                      所以这里的 `item.updatedAt === null ? '' :` 已经多余 —— 删掉，直接让它给 `'—'`
                      （本项目要求：字段拿不到显示「—」，不吞成空、不印 Invalid Date）。
                    */}
                    {` · 更新于 ${formatDateTimeLocal(item.updatedAt)}`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={playingId === item.wordId}
                    onClick={() => void preview(item.wordId)}
                  >
                    试听
                  </Button>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`删掉 ${item.wordText} 的录音`}
                      icon={<IconTrash size={16} />}
                      loading={deleteMutation.isPending}
                      onClick={() => {
                        if (window.confirm(`删掉「${item.wordText}」的录音？这个词会回到浏览器语音。`)) {
                          deleteMutation.mutate(item.wordId);
                        }
                      }}
                    >
                      删除
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
          <span className="mt-0.5 shrink-0">
            <IconRotateCcw size={14} />
          </span>
          <span>
            重录一个词：在上面搜到它、选中、录一段新的再保存 —— 旧的那段会被新的替换掉。
          </span>
        </p>
      </Card>
    </div>
  );
}
